import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate, useParams, useSearch } from '@tanstack/react-router';
import {
  Banner, Button, Checkbox, DataTable, Details, Dialog, EmptyState, Select, StatusChip,
  TextField, TopAppBar, useSnackbar,
} from '@meshble/molo';
import { api, must, type Instance } from '../lib/api';
import { ISTANZA_IN_CORSO, useIstanze, useServers } from '../lib/dati';
import { quandoRelativo } from '../lib/attivita';
import { conLeCopieSotto } from '../lib/copie';
import { ChipCopia } from './staging';
import { useBlueprint } from './blueprint';
import { BarraUtente } from '../shell';
import { usePermessi } from '../session';
import { Archive, Database, History, Plus } from '../icons';
import { Anello, CaricoSezione } from '../brand';
import { t } from '../lib/lingua';
import { ConPermesso } from '../permesso';

export { useIstanze } from '../lib/dati';

/**
 * Le istanze di un'organizzazione.
 *
 * L'elenco sta qui e non solo dentro il server perché la domanda che ci si fa
 * davanti al pannello è «dove gira *shop*», non «cosa c'è su *mcd19*».
 *
 * Le colonne sono quelle che l'hub sa davvero: la dimensione del database non
 * esiste ancora nell'API, e una colonna che mostra un trattino su ogni riga è
 * peggio di una colonna in meno. Le copie di prova non hanno una colonna: stanno
 * sotto la loro produzione, con il loro chip.
 */

/** Gli stati di `instance_status` in `00005_odoo_instances.sql`. */
export const ETICHETTA: Record<string, string> = {
  creating: 'In creazione',
  starting: 'In avvio',
  running: 'Attiva',
  stopping: 'In arresto',
  stopped: 'Ferma',
  restarting: 'Riavvio',
  modifying: 'Modifica in corso',
  restoring: 'Ripristino in corso',
  error: 'Fallita',
  deleting: 'In eliminazione',
};

/** Il colore di ciascuno: attiva è un successo, ferma è neutro, il resto lavora o è rotto. */
export const TONO_ISTANZA: Record<string, 'neutral' | 'running' | 'error' | 'success'> = {
  creating: 'running',
  starting: 'running',
  running: 'success',
  stopping: 'running',
  stopped: 'neutral',
  restarting: 'running',
  modifying: 'running',
  restoring: 'running',
  error: 'error',
  deleting: 'running',
};

/** Quelli in cui l'hub sta lavorando: finché ce n'è uno, la lista si aggiorna. */
export const IN_CORSO = ISTANZA_IN_CORSO;

const NOME_VALIDO = /^[a-z0-9][a-z0-9-]*$/;
/** Il database ammette anche l'underscore, che nei nomi PostgreSQL è di casa. */
const NOME_DB_VALIDO = /^[a-z0-9][a-z0-9_-]*$/;

/**
 * Una password forte, generata nel browser.
 *
 * Serve a far partire il campo già pieno: un'istanza risponde su un indirizzo
 * pubblico appena creata, e il nome finisce nei registri di Certificate
 * Transparency in pochi secondi. Il caso peggiore dev'essere una password che
 * nessuno conosce, mai quella predefinita di Odoo.
 *
 * L'alfabeto esclude i caratteri che si confondono leggendoli ad alta voce o
 * copiandoli a mano: zero e O, uno e l.
 */
function passwordCasuale(): string {
  const alfabeto = 'ABCDEFGHJKLMNPQRSTUVWXYZabcdefghijkmnopqrstuvwxyz23456789';
  const b = new Uint32Array(20);
  crypto.getRandomValues(b);
  return Array.from(b, (n) => alfabeto[n % alfabeto.length]).join('');
}

export function InstancesPage() {
  const { slug } = useParams({ from: '/shell/orgs/$slug/instances' });
  const { nuova } = useSearch({ from: '/shell/orgs/$slug/instances' });
  const navigate = useNavigate();
  const [apri, setApri] = useState(false);
  const permessi = usePermessi(slug);

  // Arrivare qui con `?nuova=<server>` apre subito il modulo, sul server
  // scelto: è il bottone «Nuova istanza» della scheda di un server.
  useEffect(() => {
    if (nuova) setApri(true);
  }, [nuova]);

  const q = useIstanze(slug);
  const istanze = q.data?.instances ?? [];

  return (
    <>
      <TopAppBar
        title={t('Istanze')}
        actions={
          <>
            <ConPermesso ha={permessi} serve="instance.create">
              <Button variant="filled" icon={<Plus />} onClick={() => setApri(true)}>
                {t('Nuova istanza')}
              </Button>
            </ConPermesso>
            <BarraUtente slug={slug} />
          </>
        }
      />
      <CaricoSezione attiva={q.isPending} />
      <div className="md-content">
        {q.error && (
          <Banner tone="error" title={t('Non riesco a leggere le istanze')}>{q.error.message}</Banner>
        )}

        {q.isSuccess && istanze.length === 0 && (
          <EmptyState
            icon={<Database />}
            title={t('Nessuna istanza ancora')}
            action={
              <ConPermesso ha={permessi} serve="instance.create">
                <Button variant="filled" icon={<Plus />} onClick={() => setApri(true)}>
                  {t('Crea la prima istanza')}
                </Button>
              </ConPermesso>
            }
          >
            {t("Un'istanza è un Odoo completo, con il suo database, che gira su uno dei tuoi server. Crearla richiede un paio di minuti e la puoi seguire passo per passo.")}
          </EmptyState>
        )}

        {istanze.length > 0 && (
          <DataTable
            columns={[
              { key: 'nome', header: t('Istanza') },
              { key: 'indirizzo', header: t('Indirizzo') },
              { key: 'server', header: 'Server' },
              { key: 'versione', header: 'Odoo' },
              { key: 'stato', header: t('Stato') },
              { key: 'creata', header: t('Creata'), nowrap: true },
              { key: 'azioni', header: '' },
            ]}
            rowKey={(r) => String(r.id)}
            onRowClick={(r) => navigate({ to: '/orgs/$slug/instances/$id', params: { slug, id: String(r.id) } })}
            rows={conLeCopieSotto(istanze).map((i) => ({
              id: i.id,
              nome: <Nome istanza={i} />,
              indirizzo: <Indirizzo istanza={i} />,
              server: i.serverName ?? '—',
              versione: i.odooMajor,
              stato: <StatusChip status={i.status} label={t(ETICHETTA[i.status] ?? i.status)} tone={TONO_ISTANZA[i.status]} />,
              creata: quandoRelativo(i.createdAt),
              azioni: <Azioni istanza={i} slug={slug} />,
            }))}
          />
        )}
      </div>

      <NuovaIstanza
        slug={slug}
        open={apri}
        serverIniziale={nuova}
        onOpenChange={(v) => {
          setApri(v);
          // Chiuso il modulo, il parametro se ne va: altrimenti ricaricando la
          // pagina il modulo si riaprirebbe da solo.
          if (!v && nuova) navigate({ to: '/orgs/$slug/instances', params: { slug }, search: {}, replace: true });
        }}
      />
    </>
  );
}

export function Nome({ istanza }: { istanza: Instance }) {
  if (!istanza.staging) return <span className="md-body-large">{istanza.name}</span>;
  // Rientrata sotto la produzione quando c'è; orfana resta a filo, col chip.
  return (
    <span className="md-stack__inline" style={{ alignItems: 'center', paddingInlineStart: istanza.staging.fromId ? 'var(--md-sys-spacing-4)' : 0 }}>
      {istanza.staging.fromId && <span aria-hidden="true" style={{ color: 'var(--md-sys-color-on-surface-variant)' }}>↳</span>}
      <span className="md-body-large">{istanza.name}</span>
      <ChipCopia staging={istanza.staging} />
    </span>
  );
}

/**
 * L'indirizzo di piattaforma, come link. Senza — il server non ha un dominio
 * di piattaforma — si dice, invece di lasciare una cella vuota che sembra un
 * dato mancante.
 */
function Indirizzo({ istanza }: { istanza: Instance }) {
  if (!istanza.url) {
    return <span className="md-body-small" style={{ color: 'var(--md-sys-color-on-surface-variant)' }}>{t('solo in locale')}</span>;
  }
  const host = istanza.url.replace(/^https:\/\//, '');
  return istanza.status === 'running'
    ? <a href={istanza.url} target="_blank" rel="noreferrer">{host}</a>
    : <span className="md-mono">{host}</span>;
}

/**
 * Da qui solo ciò che non sta nella scheda: seguire un'attività in corso, o
 * andare ai backup. Tutto il resto — indirizzi, risorse, log — è nella
 * scheda, che si apre cliccando la riga.
 */
function Azioni({ istanza, slug }: { istanza: Instance; slug: string }) {
  const navigate = useNavigate();
  const inCorso = IN_CORSO.has(istanza.status);
  return (
    <div className="md-stack__inline" style={{ justifyContent: 'flex-end' }}>
      {istanza.jobId && (inCorso || istanza.status === 'error') && (
        <Button
          variant="text"
          icon={<History />}
          onClick={() => navigate({ to: '/orgs/$slug/jobs/$id', params: { slug, id: istanza.jobId! } })}
        >
          {inCorso ? t('Segui') : t('Cosa è successo')}
        </Button>
      )}
      {/* Le copie non si salvano: si rifanno dalla produzione. */}
      {istanza.status === 'running' && !istanza.staging && (
        <Button
          variant="text"
          icon={<Archive />}
          onClick={() => navigate({ to: '/orgs/$slug/backup', params: { slug }, search: { istanza: istanza.id } })}
        >
          {t('Backup')}
        </Button>
      )}
    </div>
  );
}

/**
 * Tre campi, quindi un dialogo e non una pagina.
 *
 * L'API ne accetta cinque, ma `dbName` di default è il nome e `workers` lo
 * decide l'hub: chiederli qui vorrebbe dire far scegliere a chi crea un'istanza
 * due valori che nel 99% dei casi non deve toccare.
 *
 * Solo i server `ready` compaiono: l'hub rifiuta gli altri (`ErrNotReady`), e
 * una lista che li offre e poi fallisce è una lista che mente.
 */
function NuovaIstanza({
  slug, open, serverIniziale, onOpenChange,
}: { slug: string; open: boolean; serverIniziale?: string; onOpenChange: (v: boolean) => void }) {
  const qc = useQueryClient();
  const navigate = useNavigate();
  const avvisa = useSnackbar();
  const servers = useServers(slug);
  const pronti = (servers.data?.servers ?? []).filter((s) => s.status === 'ready');

  const [serverId, setServerId] = useState('');
  const [name, setName] = useState('');
  // Le versioni le dice l'hub, con l'etichetta che spiega come stanno: una
  // major nuova non è un rilascio del pannello. Si propone la consigliata.
  const versioni = useQuery({
    queryKey: ['odoo-versions'],
    queryFn: () => must(api.GET('/api/v1/odoo/versions')),
    staleTime: Infinity,
  });
  const major = versioni.data ?? [];
  const [scelta, setScelta] = useState('');
  const odooMajor = scelta || major.find((v) => v.recommended)?.major || major[0]?.major || '';
  const [dbName, setDbName] = useState('');
  // La password nasce già forte e visibile: chi la vuole sua la sostituisce,
  // chi non ci pensa non lascia comunque un'istanza con la password
  // predefinita di Odoo su un indirizzo pubblico.
  const [adminPassword, setAdminPassword] = useState(passwordCasuale);
  const [enterprise, setEnterprise] = useState(false);
  const puoEnterprise = usePermessi(slug)('instance.update');
  // I blueprint, se chi crea li vede: chi non può collegare repository non
  // ha la scelta.
  const blueprint = useBlueprint(slug, open);
  const [blueprintId, setBlueprintId] = useState('');

  const scelto = serverId || serverIniziale || pronti[0]?.id || '';
  // Il server nel titolo, non solo nel campo: con due server il campo passa
  // inosservato, e un'istanza è finita sulla macchina sbagliata (rilievo 5
  // del DR del 18/09/2026). Il titolo lo si legge prima di premere «Crea».
  const nomeServer = pronti.find((s) => s.id === scelto)?.name;

  const crea = useMutation({
    mutationFn: () =>
      must(
        api.POST('/api/v1/orgs/{slug}/instances', {
          params: { path: { slug } },
          body: {
            serverId: scelto, name, odooMajor,
            // Vuoto vuol dire «come il nome» e «generala tu»: l'hub decide, e
            // decide bene, invece di ricevere una stringa vuota da usare.
            ...(dbName.trim() && dbName.trim() !== name ? { dbName: dbName.trim() } : {}),
            ...(adminPassword ? { adminPassword } : {}),
            ...(enterprise ? { enterprise: true } : {}),
            ...(blueprintId ? { blueprintId } : {}),
          },
        }),
      ),
    onSuccess: (istanza) => {
      qc.invalidateQueries({ queryKey: ['instances', slug] });
      onOpenChange(false);
      setName('');
      setDbName('');
      setAdminPassword(passwordCasuale());
      setEnterprise(false);
      setBlueprintId('');
      avvisa(t('Sto creando {nome}: ci vogliono un paio di minuti', { nome: istanza.name }));
      // La creazione è un job lungo: si va dove si vede, non si torna a una
      // riga che dirà "In creazione" per due minuti.
      if (istanza.jobId) {
        navigate({ to: '/orgs/$slug/jobs/$id', params: { slug, id: istanza.jobId } });
      }
    },
  });

  const nomeOk = NOME_VALIDO.test(name) && name.length >= 2 && name.length <= 63;
  const dbOk = dbName.trim() === '' || NOME_DB_VALIDO.test(dbName.trim());
  const passwordOk = adminPassword.length >= 10;
  const pronto = nomeOk && dbOk && passwordOk && pronti.length > 0 && odooMajor !== '' && !crea.isPending;

  return (
    <Dialog
      open={open}
      onOpenChange={onOpenChange}
      title={nomeServer ? t('Nuova istanza su {server}', { server: nomeServer }) : t('Nuova istanza')}
      description={nomeServer && pronti.length > 1
        ? t('Un Odoo nuovo, con il suo database, sul server {server}. Se non è quello giusto, cambialo qui sotto.', { server: nomeServer })
        : t('Un Odoo nuovo, con il suo database, su uno dei tuoi server. Pronto in un paio di minuti.')}
      actions={
        <>
          <Button variant="text" onClick={() => onOpenChange(false)}>{t('Annulla')}</Button>
          <Button variant="filled" icon={crea.isPending ? <Anello /> : undefined} disabled={!pronto} onClick={() => crea.mutate()}>
            {crea.isPending ? t('Creo…') : t('Crea istanza')}
          </Button>
        </>
      }
    >
      <form
        className="md-form"
        onSubmit={(e) => {
          e.preventDefault();
          if (pronto) crea.mutate();
        }}
      >
        {crea.error && (
          <Banner tone="error" title={t('Non è stata creata')}>{(crea.error as Error).message}</Banner>
        )}

        {servers.isSuccess && pronti.length === 0 ? (
          <Banner tone="error" title={t('Nessun server pronto')}>
            {t("Un'istanza ha bisogno di un server che abbia finito l'installazione. Collegane uno e aspetta che diventi «Pronto».")}
          </Banner>
        ) : (
          <Select
            label="Server"
            value={scelto}
            options={pronti.map((s) => ({ value: s.id, label: s.name }))}
            onChange={(e) => setServerId(e.target.value)}
            help={pronti.length > 1 ? t('Hai più di un server: controlla che sia quello giusto, l’istanza non si sposta.') : undefined}
          />
        )}

        <TextField
          label={t('Nome')}
          value={name}
          autoFocus
          onChange={(e) => setName(e.target.value.toLowerCase())}
          error={name.length > 0 && !nomeOk}
          help={
            name.length > 0 && !nomeOk
              ? t('Solo minuscole, cifre e trattini, a partire da una lettera o una cifra.')
              : t('Breve e riconoscibile, es. «acme» o «acme-test». Non si cambia più.')
          }
        />

        <Select
          label={t('Versione di Odoo')}
          value={odooMajor}
          options={major.map((v) => ({ value: v.major, label: v.label }))}
          onChange={(e) => setScelta(e.target.value)}
        />

        <TextField
          label={t("Password dell'amministratore")}
          value={adminPassword}
          onChange={(e) => setAdminPassword(e.target.value)}
          error={adminPassword.length > 0 && adminPassword.length < 10}
          help={
            adminPassword.length > 0 && adminPassword.length < 10
              ? t('Almeno dieci caratteri.')
              : t('Con questa si entra in Odoo come «admin». Ne abbiamo già generata una forte: sostituiscila se preferisci la tua, e copiala prima di creare.')
          }
        />

        {/* Enterprise alla nascita chiede quello che chiede accenderla dopo:
            `instance.update`, cioè un admin. */}
        {puoEnterprise && (
          <Checkbox
            checked={enterprise}
            onCheckedChange={(v) => setEnterprise(v === true)}
            label={t('Con il codice Enterprise di Odoo')}
          />
        )}
        {enterprise && (
          <p className="md-body-small" style={{ margin: 0, color: 'var(--md-sys-color-on-surface-variant)' }}>
            {t('Serve la credenziale Enterprise dell’azienda, che si collega in Impostazioni. Senza, la creazione si ferma prima di cominciare invece di lasciarti un’istanza a metà.')}
          </p>
        )}

        {(blueprint.data?.blueprints ?? []).length > 0 && (
          <Select
            label={t('Blueprint|campo')}
            value={blueprintId}
            options={[
              { value: '', label: t('Nessuno') },
              ...(blueprint.data?.blueprints ?? []).map((b) => ({ value: b.id, label: b.name })),
            ]}
            onChange={(e) => setBlueprintId(e.target.value)}
            help={blueprintId
              ? t('Appena l’istanza è accesa installo il blueprint, con un lavoro suo: se non va, l’istanza resta e lo riapplichi dalla sua scheda.')
              : t('Un pacchetto di moduli da installare appena l’istanza è pronta.')}
          />
        )}

        <Details summary={t('Nome del database')}>
          <TextField
            label={t('Nome del database')}
            value={dbName}
            onChange={(e) => setDbName(e.target.value.toLowerCase())}
            placeholder={name || t('come il nome dell’istanza')}
            error={dbName.length > 0 && !NOME_DB_VALIDO.test(dbName)}
            help={
              dbName.length > 0 && !NOME_DB_VALIDO.test(dbName)
                ? t('Solo minuscole, cifre, trattini e underscore, a partire da una lettera o una cifra.')
                : t('Lascialo vuoto e prende il nome dell’istanza. Si cambia solo se il database deve chiamarsi diversamente, per esempio arrivando da un altro sistema.')
            }
          />
        </Details>
      </form>
    </Dialog>
  );
}
